import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, MERCHANT_ROUTES, useAuthStore, useUpgradeStore, PLAN_LIMITS } from '@dukene/shared';
import { 
  Building2, 
  Globe, 
  Check, 
  AlertCircle, 
  ArrowRight,
  Loader2,
  Sparkles
} from 'lucide-react';

export default function NewStore() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { open: openUpgrade } = useUpgradeStore();
  const setActiveStore = useAuthStore(state => state.setActiveStore);
  const [name, setName] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Fetch store count for defensive gating
  const { data: stores } = useQuery<any[]>({
    queryKey: ['merchant-stores'],
    queryFn: async () => apiClient.get<any[]>('/stores/merchant/stores'),
  });

  const mutation = useMutation({
    mutationFn: async (data: { name: string; subdomain: string }) => {
      return apiClient.post<{ success: boolean; data: any }>('/stores/merchant/store', data);
    },
    onSuccess: (result: any) => {
      try {
        const newStore = result.data;
        if (newStore?.id) {
          // 1. Update the global active store ID (sync)
          setActiveStore(newStore.id);
          
          // 2. Invalidate ALL queries so they refetch with the new store context in headers
          queryClient.invalidateQueries();
          
          // 3. Specifically invalidate the stores list to show the new one in dropdown
          queryClient.invalidateQueries({ queryKey: ['merchant-stores'] });
        }
        
        navigate(MERCHANT_ROUTES.DASHBOARD);
      } catch (err) {
        console.error('Auto-switch failed:', err);
        // Fallback: just invalidate the list and go to dashboard
        queryClient.invalidateQueries({ queryKey: ['merchant-stores'] });
        navigate(MERCHANT_ROUTES.DASHBOARD);
      }
    },
    onError: (err: any) => {
      // If the limit was reached, the global interceptor already opened the modal.
      const errorCode = err.rawError?.error?.code || err.rawError?.code;
      if (errorCode === 'STORE_LIMIT_REACHED') return;

      setError(err.message || 'Failed to create store. Please try again.');
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!name || !subdomain) {
      setError('Please fill in all fields');
      return;
    }

    // Defensive Plan Limit Check
    const currentPlan = user?.plan || 'BASIC';
    const limit = PLAN_LIMITS[currentPlan] || 1;
    const currentCount = stores?.length || 0;

    if (currentCount >= limit) {
      openUpgrade('store_limit');
      return;
    }

    mutation.mutate({ name, subdomain: subdomain.toLowerCase() });
  };

  return (
    <div className="max-w-2xl mx-auto py-12 px-4">
      <div className="bg-white border border-gray-200 rounded-3xl overflow-hidden shadow-sm">
        <div className="bg-black p-5 sm:p-8 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
            <Building2 className="w-32 h-32" />
          </div>
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-white/80 text-xs font-bold uppercase tracking-wider mb-4">
              <Sparkles className="w-3 h-3" />
              Grow your empire
            </div>
            <h1 className="text-2xl sm:text-4xl font-black tracking-tight">Create New Store</h1>
            <p className="text-white/60 mt-2 text-sm sm:text-lg font-medium">Launch your next venture in seconds.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-5 sm:p-8 space-y-6 sm:space-y-8">
          {error && (
            <div className="bg-red-50 border-2 border-red-100 text-red-900 px-6 py-4 rounded-2xl flex items-center gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <p className="font-bold text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-black text-black uppercase tracking-wider flex items-center gap-2">
                Store Name
                <Check className="w-4 h-4 text-emerald" />
              </label>
              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-black transition-colors">
                  <Building2 className="w-5 h-5" />
                </div>
                <input
                  type="text"
                  placeholder="e.g. Acme Clothing"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full h-14 bg-gray-50 border-2 border-gray-100 rounded-2xl pl-12 pr-4 text-black font-semibold focus:bg-white focus:border-black focus:outline-none transition-all text-sm sm:text-base"
                  required
                />
              </div>
              <p className="text-xs text-gray-400 font-medium px-1">Give your brand a name people will remember.</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-black text-black uppercase tracking-wider flex items-center gap-2">
                Store Subdomain
                <Globe className="w-4 h-4 text-emerald" />
              </label>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <div className="relative flex-1 group">
                  <input
                    type="text"
                    placeholder="acme-store"
                    value={subdomain}
                    onChange={(e) => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    className="w-full h-14 bg-gray-50 border-2 border-gray-100 rounded-2xl px-4 text-black font-bold text-base sm:text-lg focus:bg-white focus:border-black focus:outline-none transition-all text-left sm:text-right pr-4 sm:pr-6"
                    required
                  />
                </div>
                <span className="text-base sm:text-xl font-black text-black text-center sm:text-left py-1 sm:py-0">.dukene.com</span>
              </div>
              <p className="text-xs text-gray-400 font-medium px-1">Your free web address. No spaces allowed.</p>
            </div>
          </div>

          <div className="pt-4 flex flex-col items-center gap-4">
            <button
              type="submit"
              disabled={mutation.isPending || stores === undefined}
              className="w-full h-16 bg-black text-white rounded-2xl font-black text-lg hover:bg-gray-800 transition-all shadow-xl hover:shadow-black/20 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-1 active:translate-y-0"
            >
              {mutation.isPending ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin" />
                  Creating Store...
                </>
              ) : (
                <>
                  Launch Store
                  <ArrowRight className="w-6 h-6" />
                </>
              )}
            </button>
            
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="text-sm font-bold text-gray-400 hover:text-black transition-colors"
            >
              Cancel and go back
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
